import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import PageHeader from "../components/practice/PageHeader";
import AreaHero from "../components/practice/AreaHero";
import { AreaServices, AreaFaqs, AreaAttorneys, RelatedAreas, AreaCta } from "../components/practice/AreaSections";
import Footer from "../components/Footer";
import SiteLoader from "../components/SiteLoader";
import SiteError from "../components/SiteError";
import StructuredData, { practiceAreaSchema } from "../components/StructuredData";
import { ModalsProvider } from "../contexts/ModalsContext";
import { useContent } from "../contexts/ContentContext";
import { useSeo } from "../hooks/useSeo";

function NotFound() {
  return (
    <div className="min-h-screen bg-[#fbf9f6] flex flex-col items-center justify-center text-center px-6" data-testid="practice-not-found">
      <h1 className="font-serif text-4xl text-[#1a2c4e]">Practice area not found</h1>
      <Link to="/" className="mt-6 text-[#b8924a] underline underline-offset-4">Return to the home page</Link>
    </div>
  );
}

function seoFor(area, path) {
  if (!area) return { title: "Page Not Found | HBHS Law", noindex: true, path };
  return { title: area.seo?.title || area.title, description: area.seo?.description || area.summary, path };
}

function joinAttorneys(area, attorneys) {
  return (area.attorneys || [])
    .map((m) => {
      const a = attorneys.find((x) => x.id === m.attorneyId);
      return a ? { ...a, blurb: m.blurb } : null;
    })
    .filter(Boolean);
}

function AreaContent({ area, firm, attorneys, practiceAreas }) {
  const areaAttorneys = joinAttorneys(area, attorneys);
  return (
    <ModalsProvider>
      <div className="bg-[#fbf9f6] paper-bg min-h-screen" data-testid="practice-area-page">
        <PageHeader areaId={area.id} areaTitle={area.title} />
        <AreaHero area={area} />
        <main className="max-w-6xl mx-auto px-6 py-14 md:py-20">
          <div className="max-w-4xl">
            {area.page?.intro && <p className="text-[#1a2c4e] font-serif text-xl md:text-2xl leading-relaxed" data-testid="area-intro">{area.page.intro}</p>}
            <p className="text-[#3a4d6b] text-[17px] leading-relaxed mt-6" data-testid="area-description">{area.description}</p>
            <AreaServices title={area.title} services={area.page?.services} />
            <AreaAttorneys title={area.title} list={areaAttorneys} />
            <AreaFaqs faqs={area.page?.faqs} />
            <AreaCta areaId={area.id} areaTitle={area.title} phone={firm.phones?.[0]} />
            <RelatedAreas areas={practiceAreas} currentId={area.id} />
          </div>
        </main>
        <Footer />
        <StructuredData data={practiceAreaSchema(area, firm, areaAttorneys)} />
      </div>
    </ModalsProvider>
  );
}

export default function PracticeAreaPage() {
  const { slug } = useParams();
  const { firm, practiceAreas, attorneys, loading, error, refresh } = useContent();
  const area = practiceAreas.find((a) => a.id === slug);
  const moved = !area && practiceAreas.find((a) => (a.previousIds || []).includes(slug));

  useSeo(seoFor(area, `/practice-areas/${slug}`));

  if (loading) return <SiteLoader />;
  if (error || !firm) return <SiteError onRetry={refresh} />;
  if (moved) return <Navigate to={`/practice-areas/${moved.id}`} replace />;
  if (!area) return <NotFound />;

  return <AreaContent area={area} firm={firm} attorneys={attorneys} practiceAreas={practiceAreas} />;
}
